import { API_BASE_URL, HAS_API, requestJson } from './apiConfig'
import { DEVELOPMENT_CONFIG } from './installService'
import { checkAdminSlug } from '../data/reservedRoutes'
import type {
  BrandingAsset,
  DatabaseHealth,
  LocalizationSettings,
  PublicPlatformConfig,
  SecuritySettings,
  SettingsChange,
  SettingsSection,
  SystemInformation,
} from '../types/install'

/**
 * The platform settings seam.
 *
 * Everything the installer asked for once is editable here afterwards — with
 * two deliberate exceptions that are not settings at all:
 *
 *   • Database credentials. They belong to deployment, not to a browser. This
 *     module can read the database's *health*, and there is no function here
 *     capable of returning or changing a credential.
 *   • Session and encryption secrets. They have no representation in any type
 *     this module touches, so there is no path by which one could be rendered.
 *
 * Reads report their source the same way design variant configuration does:
 * 'server' when the configuration service answered, 'local' when we are falling
 * back to the last thing this browser stored, 'defaults' when there is nothing
 * but development defaults. The console labels itself from that, so a local
 * change is never presented as a published one.
 */

const SETTINGS = '/api/admin/settings'
const HISTORY = '/api/admin/settings/history'
const HEALTH = '/api/admin/system/database'
const SYSTEM = '/api/admin/system/information'
const UPLOAD = '/api/admin/settings/branding'

const LOCAL_KEY = 'campuzon.platform.settings.v1'
const LOCAL_HISTORY_KEY = 'campuzon.platform.settings.history.v1'

export const SETTINGS_ARE_SERVER_BACKED = HAS_API

export const DEFAULT_LOCALIZATION: LocalizationSettings = {
  timezone: 'Africa/Lagos',
  currency: 'NGN',
  locale: 'en-NG',
  country: 'NG',
  dateFormat: 'd MMM yyyy',
  timeFormat: '24h',
  measurement: 'metric',
  weekStartsOn: 'monday',
}

export const DEFAULT_SECURITY: SecuritySettings = {
  secretsGenerated: false,
  sessionHours: 12,
  passwordMinLength: 12,
  requireMfaForAdmins: true,
  loginAttemptsBeforeThrottle: 5,
  apiRequestsPerMinute: 120,
  secureCookies: true,
  sameSite: 'lax',
}

export interface SettingsBundle {
  config: PublicPlatformConfig
  security: SecuritySettings
}

export interface SettingsResult extends SettingsBundle {
  source: 'server' | 'local' | 'defaults'
  error: string | null
}

const DEFAULT_BUNDLE: SettingsBundle = {
  config: { ...DEVELOPMENT_CONFIG, localization: DEFAULT_LOCALIZATION },
  security: DEFAULT_SECURITY,
}

/** A stored bundle is a claim: validated before it is allowed to render. */
function usable(value: unknown): value is SettingsBundle {
  const bundle = value as SettingsBundle | null
  return Boolean(
    bundle?.config?.platform?.name &&
      bundle.config.seo?.metaTitle &&
      bundle.config.adminSlug &&
      bundle.security,
  )
}

function readLocal(): SettingsBundle | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return usable(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeLocal(bundle: SettingsBundle): void {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(bundle))
  } catch {
    // A browser refusing storage is not a reason to fail a save that already
    // reached the server.
  }
}

function normalise(bundle: SettingsBundle): SettingsBundle {
  return {
    config: {
      ...bundle.config,
      localization: { ...DEFAULT_LOCALIZATION, ...(bundle.config.localization ?? {}) },
    },
    security: { ...DEFAULT_SECURITY, ...bundle.security },
  }
}

export async function loadSettings(): Promise<SettingsResult> {
  if (!HAS_API) {
    const local = readLocal()
    return local
      ? { ...normalise(local), source: 'local', error: null }
      : { ...DEFAULT_BUNDLE, source: 'defaults', error: null }
  }
  try {
    const bundle = await requestJson<SettingsBundle>(SETTINGS)
    if (!usable(bundle)) throw new Error('The settings response was incomplete.')
    const clean = normalise(bundle)
    writeLocal(clean)
    return { ...clean, source: 'server', error: null }
  } catch (failure) {
    // Degrade to the last configuration we actually saw rather than jumping
    // back to development defaults, which would silently change the product.
    const local = readLocal()
    const message =
      failure instanceof Error ? failure.message : 'The configuration service did not answer.'
    return local
      ? { ...normalise(local), source: 'local', error: message }
      : { ...DEFAULT_BUNDLE, source: 'defaults', error: message }
  }
}

/**
 * Saves one section. Sections are saved individually and deliberately: a
 * half-applied settings write should never be able to mix a new site title with
 * an old security policy.
 */
export async function saveSection(
  section: SettingsSection,
  value: unknown,
  context: { reason: string; changedBy: string; current: SettingsBundle },
): Promise<SettingsBundle> {
  if (context.reason.trim().length < 4) {
    throw new Error('A reason is required. It is recorded with the change.')
  }

  if (section === 'admin-route') {
    const verdict = checkAdminSlug(String(value ?? ''))
    if (!verdict.ok) throw new Error(verdict.reason ?? 'That administration URL is not allowed.')
  }

  const next = applyLocally(section, value, context.current)

  if (!HAS_API) {
    writeLocal(next)
    recordLocalHistory(section, context, next)
    return next
  }

  const stored = await requestJson<SettingsBundle>(`${SETTINGS}/${section}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value, reason: context.reason }),
  })
  if (!usable(stored)) throw new Error('The service did not return the stored configuration.')
  const clean = normalise(stored)
  writeLocal(clean)
  return clean
}

function applyLocally(
  section: SettingsSection,
  value: unknown,
  current: SettingsBundle,
): SettingsBundle {
  switch (section) {
    case 'platform':
      return { ...current, config: { ...current.config, platform: value as never } }
    case 'seo':
    case 'branding':
      // Branding is part of the site's public presentation, so it lives on the
      // same record rather than a parallel one that could disagree with it.
      return {
        ...current,
        config: { ...current.config, seo: { ...current.config.seo, ...(value as object) } },
      }
    case 'localization':
      return {
        ...current,
        config: {
          ...current.config,
          localization: value as LocalizationSettings,
          platform: {
            ...current.config.platform,
            timezone: (value as LocalizationSettings).timezone,
            currency: (value as LocalizationSettings).currency,
            locale: (value as LocalizationSettings).locale,
            country: (value as LocalizationSettings).country,
          },
        },
      }
    case 'security':
      return { ...current, security: { ...current.security, ...(value as object) } }
    case 'admin-route':
      return { ...current, config: { ...current.config, adminSlug: String(value) } }
    case 'terminology':
      // Stored whole rather than merged: an administrator resetting a concept to
      // its default expects the override to be *gone*, not merged back in.
      return {
        ...current,
        config: { ...current.config, terminology: value as PublicPlatformConfig['terminology'] },
      }
    default:
      return current
  }
}

function recordLocalHistory(
  section: SettingsSection,
  context: { reason: string; changedBy: string; current: SettingsBundle },
  next: SettingsBundle,
): void {
  const entry: SettingsChange = {
    id: `local-${Date.now()}`,
    section,
    changedBy: context.changedBy,
    reason: context.reason,
    changed: diff(section, context.current, next),
    createdAt: new Date().toISOString(),
  }
  try {
    const raw = window.localStorage.getItem(LOCAL_HISTORY_KEY)
    const list: SettingsChange[] = raw ? JSON.parse(raw) : []
    window.localStorage.setItem(LOCAL_HISTORY_KEY, JSON.stringify([entry, ...list].slice(0, 50)))
  } catch {
    // History is evidence, not a dependency of the save.
  }
}

/** Only the fields that actually differed, as they are displayed. */
export function diff(
  section: SettingsSection,
  before: SettingsBundle,
  after: SettingsBundle,
): Array<{ field: string; from: string; to: string }> {
  const pick = (bundle: SettingsBundle): Record<string, unknown> => {
    switch (section) {
      case 'platform':
        return bundle.config.platform as unknown as Record<string, unknown>
      case 'seo':
      case 'branding':
        return bundle.config.seo as unknown as Record<string, unknown>
      case 'localization':
        return bundle.config.localization as unknown as Record<string, unknown>
      case 'security':
        return bundle.security as unknown as Record<string, unknown>
      case 'admin-route':
        return { adminSlug: bundle.config.adminSlug }
      case 'terminology': {
        // Flattened to concept.form so the history reads as the change actually
        // made: "housing.plural: Housing → Lodging".
        const flat: Record<string, unknown> = {}
        for (const [concept, forms] of Object.entries(bundle.config.terminology ?? {})) {
          for (const [form, word] of Object.entries(forms ?? {})) {
            if (word) flat[`${concept}.${form}`] = word
          }
        }
        return flat
      }
      default:
        return {}
    }
  }
  const from = pick(before)
  const to = pick(after)
  // The union of both sides: a field that was *removed* is as much a change as
  // one that was added, and reporting only the new keys would hide a reset.
  return Array.from(new Set([...Object.keys(from), ...Object.keys(to)]))
    .filter((key) => String(from[key] ?? '') !== String(to[key] ?? ''))
    .map((key) => ({ field: key, from: String(from[key] ?? '—'), to: String(to[key] ?? '—') }))
}

export async function settingsHistory(): Promise<SettingsChange[]> {
  if (!HAS_API) {
    try {
      const raw = window.localStorage.getItem(LOCAL_HISTORY_KEY)
      return raw ? (JSON.parse(raw) as SettingsChange[]) : []
    } catch {
      return []
    }
  }
  try {
    return await requestJson<SettingsChange[]>(HISTORY)
  } catch {
    return []
  }
}

/**
 * Uploads a branding asset. Validation happens here for the administrator's
 * benefit and again on the server, which is the only validation that counts.
 * SVG is refused outright: it is a script container, not an image format.
 */
export const ALLOWED_BRANDING_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/x-icon']
export const MAX_BRANDING_BYTES = 2 * 1024 * 1024

export async function uploadBranding(
  kind: BrandingAsset['kind'],
  file: File,
): Promise<BrandingAsset> {
  if (!ALLOWED_BRANDING_TYPES.includes(file.type)) {
    throw new Error(
      `${file.type || 'That file type'} is not accepted. Use PNG, JPEG, WebP or ICO. SVG is refused because it can carry script.`,
    )
  }
  if (file.size > MAX_BRANDING_BYTES) {
    throw new Error(
      `${(file.size / 1024 / 1024).toFixed(1)} MB is over the 2 MB limit for branding assets.`,
    )
  }

  if (!HAS_API) {
    // With no storage service, the file is held as a data URL for this browser
    // and labelled as such by the caller. It is not an upload and is not
    // presented as one.
    const url = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => reject(new Error('The file could not be read.'))
      reader.readAsDataURL(file)
    })
    return {
      kind,
      url,
      fileName: file.name,
      contentType: file.type,
      bytes: file.size,
      uploadedAt: new Date().toISOString(),
    }
  }

  const form = new FormData()
  form.append('kind', kind)
  form.append('file', file, file.name)
  const response = await fetch(`${API_BASE_URL}${UPLOAD}`, {
    method: 'POST',
    credentials: 'include',
    // No explicit Content-Type: the browser must set the multipart boundary.
    cache: 'no-store',
    body: form,
  })
  if (!response.ok) {
    throw new Error(`The storage service refused the file (${response.status}).`)
  }
  return (await response.json()) as BrandingAsset
}

export async function databaseHealth(): Promise<DatabaseHealth> {
  if (!HAS_API) {
    return {
      connected: false,
      engine: 'PostgreSQL',
      serverVersion: null,
      hostMasked: '—',
      databaseName: '—',
      schemaVersion: null,
      lastMigration: null,
      pendingMigrations: [],
      latencyMs: null,
      checkedAt: new Date().toISOString(),
      message:
        'No configuration service is reachable from this deployment, so there is no database to report on.',
    }
  }
  return requestJson<DatabaseHealth>(HEALTH)
}

export async function systemInformation(): Promise<SystemInformation> {
  if (!HAS_API) {
    return {
      platformVersion: 'Campuzon 2.0',
      frontendVersion: 'Campuzon 2.0',
      apiVersion: null,
      runtimeVersion: null,
      databaseVersion: null,
      schemaVersion: null,
      installedAt: null,
      environment: 'development',
      apiOrigin: null,
    }
  }
  const info = await requestJson<SystemInformation>(SYSTEM)
  return { ...info, apiOrigin: API_BASE_URL }
}
